/**
 * Slice 19 — Issue triage (Missing / Broken → Restock · Vendor · Claim).
 * Open filter · list · empty · detail with three paths · assign · claiming · done.
 */
import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { isVideoMediaUrl } from "@/lib/mediaUrl";
import { MediaThumb } from "@/components/media/MediaThumb";
import { useAuth } from "@/contexts/AuthContext";
import { useProperties } from "@/hooks/useProperties";
import { useCohostProperties } from "@/hooks/useCohostProperties";
import {
  useIssuesInbox,
  useMarkIssueDone,
  useUpdateIssueTriage,
  formatIssueDetailSubline,
  formatIssueGuestLine,
  formatIssueMetaLine,
  issueNote,
  draftForIssue,
  type IssueInboxRow,
} from "@/hooks/useIssuesInbox";
import { usePropertyAssigneeOptions } from "@/hooks/usePropertyAssigneeOptions";
import { useVendors } from "@/hooks/useVendors";
import { toast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  airbnbClaimUrl,
  isIssueOpen,
  issueStatusLabel,
  type IssueTriagePath,
} from "@/lib/issueTriage";
import { IssueRestockBlock } from "@/components/owner/IssueRestockBlock";

type Filter = "Open" | "Done";

interface SimpleIssuesInboxProps {
  propertyId?: string;
  focusIssueId?: string;
  className?: string;
}

type AssignPerson = {
  id: string;
  name: string;
  role: string;
  initials: string;
};

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] ?? ""}${parts[1][0] ?? ""}`.toUpperCase();
}

function PhotoThumb({ url, className }: { url?: string | null; className?: string }) {
  return (
    <div
      className={cn(
        "shrink-0 overflow-hidden border-[1.5px] border-[#dbe4ea]",
        !url && "bg-[repeating-linear-gradient(135deg,#cbd8e1_0_10px,#dae4eb_10px_20px)]",
        className,
      )}
    >
      {url ? <MediaThumb url={url} /> : null}
    </div>
  );
}

function TypeChip({ type }: { type: "Missing" | "Broken" }) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center rounded-full px-[11px] py-1 text-xs font-bold",
        type === "Missing" ? "bg-[#fdf3e5] text-[#b45309]" : "bg-[#fbeceb] text-[#b4453a]",
      )}
    >
      {type}
    </span>
  );
}

function StatusChip({ label }: { label: string }) {
  const claiming = label === "Claiming";
  const progress = label.startsWith("In progress");
  const done = label === "Done";
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-3 py-[5px] text-[13px] font-bold",
        claiming && "bg-[#0f1720] text-white",
        progress && "bg-[#e2eeed] text-[#15776d]",
        done && "bg-[#eef2f5] text-[#475569]",
        !claiming && !progress && !done && "bg-[#e6edf1] text-[#475569]",
      )}
    >
      {label}
    </span>
  );
}

function ActionIconRestock() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 8l8-4 8 4v8l-8 4-8-4z" />
      <path d="M4 8l8 4 8-4" />
      <path d="M12 12v8" />
    </svg>
  );
}

function ActionIconVendor() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.5 6.5a4 4 0 005.2 5.2L21 13l-8 8-2-2 8-8-1.3 1.3" />
      <path d="M6.5 9.5L3 6l3-3 3.5 3.5" />
    </svg>
  );
}

function ActionIconClaim() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3.5l7 3v6c0 4-3 6.6-7 8-4-1.4-7-4-7-8v-6z" />
    </svg>
  );
}

function AssignSheet({
  people,
  pendingPath,
  onPick,
  onSkip,
  onClose,
  onCustom,
}: {
  people: AssignPerson[];
  pendingPath: IssueTriagePath | null;
  onPick: (p: AssignPerson) => void;
  onSkip: () => void;
  onClose: () => void;
  onCustom: (name: string) => void;
}) {
  const [custom, setCustom] = useState("");
  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center bg-[rgba(9,14,20,0.45)] p-0 sm:items-center sm:p-4">
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        aria-label="Close"
        onClick={onClose}
      />
      <div className="relative z-10 flex max-h-[min(88dvh,720px)] w-full max-w-[460px] flex-col rounded-t-[22px] bg-[#f4f7f9] px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-5 shadow-[0_-12px_34px_rgba(15,23,32,0.18)] sm:rounded-[22px]">
        <div className="flex shrink-0 items-center justify-between gap-3 pb-1">
          <div className="text-xl font-bold tracking-[-0.3px] text-[#0f1720]">
            Assign{pendingPath === "vendor" ? " · Vendor" : pendingPath === "restock" ? " · Restock" : ""}
          </div>
          <button type="button" onClick={onSkip} className="text-[15px] font-bold text-[#94a3b8] hover:text-[#64748b]">
            Skip
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-3 [-webkit-overflow-scrolling:touch]">
          <div className="flex flex-col gap-2">
            {people.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => onPick(p)}
                className="flex items-center gap-3 rounded-[14px] border-[1.5px] border-[#e6edf1] bg-white px-3.5 py-3 text-left hover:border-[#24a89a]"
              >
                <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-[#e2eeed] text-[13px] font-bold text-[#15776d]">
                  {p.initials}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-base font-bold text-[#0f1720]">{p.name}</div>
                  <div className="truncate text-[13px] font-medium text-[#64748b]">{p.role}</div>
                </div>
              </button>
            ))}
            {people.length === 0 ? (
              <div className="rounded-[14px] border-[1.5px] border-dashed border-[#e6edf1] bg-white px-3.5 py-6 text-center text-[14px] font-medium text-[#8494a2]">
                No people yet — type a name below, or Skip
              </div>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 gap-2 border-t border-[#e6edf1]/80 pt-3.5">
          <input
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            placeholder="Name…"
            className="h-12 flex-1 rounded-[14px] border-[1.5px] border-[#e6edf1] bg-white px-3.5 text-[15px] font-medium text-[#0f1720] outline-none focus:border-[#24a89a]"
          />
          <button
            type="button"
            disabled={!custom.trim()}
            onClick={() => onCustom(custom.trim())}
            className="h-12 shrink-0 rounded-[14px] bg-[#24a89a] px-5 text-[15px] font-bold text-white disabled:opacity-40"
          >
            Assign
          </button>
        </div>
      </div>
    </div>
  );
}

export function SimpleIssuesInbox({ propertyId, focusIssueId, className }: SimpleIssuesInboxProps) {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { data: ownerProperties = [], isLoading: ownerPropsLoading } = useProperties();
  const { data: cohostProperties = [], isLoading: cohostPropsLoading } = useCohostProperties();

  const propertyIds = useMemo(() => {
    if (propertyId) return [propertyId];
    const ids = new Set<string>([
      ...ownerProperties.map((p) => p.id),
      ...cohostProperties.map((p) => p.id),
    ]);
    return [...ids];
  }, [propertyId, ownerProperties, cohostProperties]);

  const propsLoading = !propertyId && (ownerPropsLoading || cohostPropsLoading);
  const { data: rows = [], isLoading: issuesLoading } = useIssuesInbox(
    propsLoading ? undefined : propertyIds,
  );
  const isLoading = propsLoading || issuesLoading;
  const markDone = useMarkIssueDone();
  const updateTriage = useUpdateIssueTriage();

  const [filter, setFilter] = useState<Filter>("Open");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [pendingPath, setPendingPath] = useState<IssueTriagePath | null>(null);

  useEffect(() => {
    if (!focusIssueId || rows.length === 0) return;
    if (rows.some((r) => r.id === focusIssueId)) {
      setSelectedId(focusIssueId);
      const hit = rows.find((r) => r.id === focusIssueId);
      if (hit && !isIssueOpen(hit.status)) setFilter("Done");
    }
  }, [focusIssueId, rows]);

  const visible = useMemo(
    () => rows.filter((r) => (filter === "Open" ? isIssueOpen(r.status) : !isIssueOpen(r.status))),
    [rows, filter],
  );

  const selected = rows.find((r) => r.id === selectedId) ?? null;
  const selectedOpen = selected ? isIssueOpen(selected.status) : false;
  const selectedStatus = selected
    ? issueStatusLabel(selected.status, selected.triage.path)
    : "Open";

  const assignPropertyId = selected?.property_id ?? propertyId;
  const { teamMembers, availableCleaners } = usePropertyAssigneeOptions(assignPropertyId);
  const { data: vendors = [] } = useVendors(assignPropertyId);

  const assignPeople = useMemo<AssignPerson[]>(() => {
    const list: AssignPerson[] = [];
    for (const c of availableCleaners) {
      list.push({ id: `cleaner:${c.id}`, name: c.name, role: "Cleaner", initials: initialsOf(c.name) });
    }
    for (const m of teamMembers) {
      list.push({ id: m.id, name: m.name, role: "VA", initials: initialsOf(m.name) });
    }
    for (const v of vendors) {
      list.push({
        id: `vendor:${v.id}`,
        name: v.business_name,
        role: v.vendor_type || "Vendor",
        initials: initialsOf(v.business_name),
      });
    }
    return list;
  }, [availableCleaners, teamMembers, vendors]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      toast({
        title: "Could not update",
        description: err instanceof Error ? err.message : "Try again",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const startPath = (path: "restock" | "vendor") => {
    setPendingPath(path);
    setAssignOpen(true);
  };

  const applyProgress = async (path: "restock" | "vendor", assigneeName: string | null, assigneeId?: string | null) => {
    if (!selected) return;
    await run(async () => {
      await updateTriage.mutateAsync({
        reportId: selected.id,
        propertyId: selected.property_id,
        status: "in_progress",
        clearResolved: true,
        meta: {
          v: 1,
          path,
          assignee_name: assigneeName,
          assignee_id: assigneeId ?? null,
          claim_draft: selected.triage.claim_draft ?? null,
          done_line: null,
        },
      });
      setAssignOpen(false);
      setPendingPath(null);
      toast({ title: path === "restock" ? "Restock" : "Send vendor" });
    });
  };

  const handleClaim = async () => {
    if (!selected) return;
    const draft = draftForIssue(selected);
    await run(async () => {
      await updateTriage.mutateAsync({
        reportId: selected.id,
        propertyId: selected.property_id,
        status: "claiming",
        clearResolved: true,
        meta: {
          v: 1,
          path: "claim",
          assignee_name: null,
          assignee_id: null,
          claim_draft: draft,
          done_line: null,
        },
      });
      toast({ title: "Claiming" });
    });
  };

  const handleDismiss = async () => {
    if (!selected) return;
    await run(async () => {
      await markDone.mutateAsync({
        reportId: selected.id,
        propertyId: selected.property_id,
        resolvedBy: user?.id,
        path: "dismiss",
        existingMeta: selected.triage,
      });
      toast({ title: "Done" });
      setSelectedId(null);
    });
  };

  const handleMarkDone = async () => {
    if (!selected) return;
    await run(async () => {
      await markDone.mutateAsync({
        reportId: selected.id,
        propertyId: selected.property_id,
        resolvedBy: user?.id,
        path: selected.triage.path,
        assigneeName: selected.triage.assignee_name,
        existingMeta: selected.triage,
      });
      toast({ title: "Done" });
    });
  };

  const handleReopen = async () => {
    if (!selected) return;
    await run(async () => {
      await updateTriage.mutateAsync({
        reportId: selected.id,
        propertyId: selected.property_id,
        status: "open",
        clearResolved: true,
        meta: { v: 1, path: null, assignee_name: null, assignee_id: null, claim_draft: null, done_line: null },
      });
      toast({ title: "Open" });
    });
  };

  const handleChangePath = async () => {
    if (!selected) return;
    await run(async () => {
      await updateTriage.mutateAsync({
        reportId: selected.id,
        propertyId: selected.property_id,
        status: "open",
        clearResolved: true,
        meta: {
          v: 1,
          path: null,
          assignee_name: null,
          assignee_id: null,
          claim_draft: selected.triage.claim_draft ?? null,
          done_line: null,
        },
      });
    });
  };

  const copyDraft = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: "Copied" });
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  };

  const filterBar = (
    <div
      className={cn(
        "flex gap-1 rounded-[14px] bg-white p-1",
      )}
    >
      {(["Open", "Done"] as Filter[]).map((label) => {
        const active = filter === label;
        return (
          <button
            key={label}
            type="button"
            onClick={() => setFilter(label)}
            className={cn(
              "rounded-[10px] text-[14px] transition-colors",
              isMobile ? "flex-1 py-[9px] text-center" : "px-[22px] py-[9px]",
              active ? "bg-[#15776d] font-bold text-white" : "font-bold text-[#64748b]",
            )}
          >
            {label}
          </button>
        );
      })}
    </div>
  );

  const renderListRow = (row: IssueInboxRow, mobile: boolean) => {
    const open = isIssueOpen(row.status);
    const status = issueStatusLabel(row.status, row.triage.path);
    return (
      <button
        key={row.id}
        type="button"
        onClick={() => setSelectedId(row.id)}
        className={cn(
          "w-full text-left",
          mobile
            ? cn(
                "flex items-center gap-3.5 rounded-[15px] border-[1.5px] border-[#e6edf1] bg-white px-3.5 py-3",
                !open && "border-[#f1f5f8] opacity-[0.62]",
              )
            : cn(
                "grid grid-cols-[56px_minmax(0,1fr)_auto] items-center gap-4 border-b-[1.5px] border-[#f1f5f8] px-[18px] py-3.5 last:border-b-0 hover:bg-[#f8fafc]",
                !open && "opacity-[0.62]",
              ),
        )}
      >
        <PhotoThumb url={row.photos?.[0]} className={cn("rounded-[13px]", mobile ? "h-[54px] w-[54px]" : "h-14 w-14")} />
        <div className="min-w-0 flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <TypeChip type={row.type_label} />
            {mobile ? <StatusChip label={status} /> : null}
          </div>
          <div className="truncate text-[17px] font-bold tracking-[-0.2px] text-[#0f1720] max-sm:text-base">
            {issueNote(row)}
          </div>
          <div className="truncate text-sm font-medium text-[#64748b]">{formatIssueMetaLine(row)}</div>
        </div>
        {!mobile ? <StatusChip label={status} /> : null}
      </button>
    );
  };

  const detailBody = selected ? (
    <div className="relative flex flex-col">
      <div className={cn("flex flex-col gap-[18px]", isMobile ? "px-5 py-[18px]" : "px-6 pb-6 pt-[22px]")}>
        <div className="flex items-start justify-between gap-3.5">
          <div className="flex min-w-0 flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <TypeChip type={selected.type_label} />
              <StatusChip label={selectedStatus} />
            </div>
            <div className="text-[26px] font-bold leading-tight tracking-[-0.5px] text-[#0f1720] max-sm:text-[22px]">
              {issueNote(selected)}
            </div>
            <div className="text-sm font-medium text-[#64748b]">{formatIssueDetailSubline(selected)}</div>
          </div>
          {selected.triage.assignee_name ? (
            <div className="shrink-0 rounded-full bg-[#e2eeed] px-3 py-[5px] text-[13px] font-bold text-[#15776d]">
              {selected.triage.assignee_name}
            </div>
          ) : null}
        </div>

        <div
          className={cn(
            "relative overflow-hidden rounded-[18px] border-[1.5px] border-[#dbe4ea]",
            isMobile ? "h-[220px]" : "h-[200px]",
            !(selected.photos?.[0]) &&
              "bg-[repeating-linear-gradient(135deg,#cbd8e1_0_14px,#dae4eb_14px_28px)]",
          )}
        >
          {selected.photos?.[0] ? (
            isVideoMediaUrl(selected.photos[0]) ? (
              <video
                src={selected.photos[0]}
                controls
                playsInline
                className="h-full w-full object-cover"
              />
            ) : (
              <img src={selected.photos[0]} alt="" className="h-full w-full object-cover" />
            )
          ) : null}
          <div className="absolute bottom-3 left-3 rounded-lg bg-white px-2 py-1 font-mono text-[11px] text-[#475569]">
            evidence{selected.room_name ? ` · ${selected.room_name}` : ""}
          </div>
        </div>

        <div className="flex items-center gap-2.5 text-sm font-medium text-[#475569]">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="5" width="16" height="15" rx="3" />
            <path d="M8 3.5v3M16 3.5v3M4 10h16" />
          </svg>
          <span>{formatIssueGuestLine(selected)}</span>
        </div>

        {/* Open — three actions */}
        {selected.triage_status === "open" && selectedOpen ? (
          <div className="flex flex-col gap-2.5">
            <button
              type="button"
              disabled={busy}
              onClick={() => startPath("restock")}
              className="flex h-[66px] items-center justify-center gap-2.5 rounded-[18px] bg-[#24a89a] text-[21px] font-bold text-white hover:bg-[#1f958a] max-sm:h-[58px] max-sm:text-[18px]"
            >
              <ActionIconRestock />
              Restock
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => startPath("vendor")}
              className="flex h-[66px] items-center justify-center gap-2.5 rounded-[18px] border-2 border-[#24a89a] bg-white text-[21px] font-bold text-[#15776d] hover:bg-[#f2faf9] max-sm:h-[58px] max-sm:text-[18px]"
            >
              <ActionIconVendor />
              Send vendor
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleClaim()}
              className="flex h-[66px] items-center justify-center gap-2.5 rounded-[18px] border-2 border-[#e6edf1] bg-white text-[21px] font-bold text-[#0f1720] hover:border-[#b4453a] hover:text-[#b4453a] max-sm:h-[58px] max-sm:text-[18px]"
            >
              <ActionIconClaim />
              File claim
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleDismiss()}
              className="py-2 text-center text-[15px] font-bold text-[#94a3b8] hover:text-[#64748b]"
            >
              Dismiss
            </button>
          </div>
        ) : null}

        {/* In progress */}
        {selected.triage_status === "in_progress" && selectedOpen ? (
          <div className="flex flex-col gap-2.5">
            {selected.triage.path === "restock" ? (
              <IssueRestockBlock
                propertyId={selected.property_id}
                issueReportId={selected.id}
                itemLabel={issueNote(selected)}
                disabled={busy}
              />
            ) : null}
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleMarkDone()}
              className="flex h-[58px] items-center justify-center gap-2.5 rounded-[18px] bg-[#24a89a] text-[18px] font-bold text-white hover:bg-[#1f958a]"
            >
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                <>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12.5l4.5 4.5L19 7.5" />
                  </svg>
                  Mark done
                </>
              )}
            </button>
            <div className="flex gap-2.5">
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setPendingPath(selected.triage.path === "vendor" ? "vendor" : "restock");
                  setAssignOpen(true);
                }}
                className="flex h-12 flex-1 items-center justify-center rounded-[14px] border-[1.5px] border-[#e6edf1] bg-white text-[15px] font-bold text-[#15776d] hover:border-[#24a89a]"
              >
                Assign
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void handleChangePath()}
                className="flex h-12 flex-1 items-center justify-center rounded-[14px] border-[1.5px] border-[#e6edf1] bg-white text-[15px] font-bold text-[#15776d] hover:border-[#24a89a]"
              >
                Change path
              </button>
            </div>
          </div>
        ) : null}

        {/* Claiming */}
        {selected.triage_status === "claiming" && selectedOpen ? (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-2 rounded-[16px] border-[1.5px] border-[#e6edf1] bg-[#f8fafc] px-4 py-3.5">
              <div className="text-xs font-bold tracking-[0.7px] text-[#64748b]">CLAIM DRAFT (OPTIONAL)</div>
              <div className="text-[15px] font-medium leading-[1.5] text-[#334155]">
                {draftForIssue(selected)}
              </div>
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => void copyDraft(draftForIssue(selected))}
                  className="text-[13px] font-bold text-[#15776d]"
                >
                  Copy
                </button>
              </div>
            </div>
            <a
              href={airbnbClaimUrl(selected.reservation_external_id)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-[58px] items-center justify-center gap-2.5 rounded-[18px] bg-[#0f1720] text-[18px] font-bold text-white hover:bg-[#1d2836]"
            >
              Open Airbnb
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 16L16 8M9.5 8H16v6.5" />
              </svg>
            </a>
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleMarkDone()}
              className="flex h-12 items-center justify-center rounded-[14px] border-[1.5px] border-[#e6edf1] bg-white text-[15px] font-bold text-[#15776d] hover:border-[#24a89a]"
            >
              Mark done
            </button>
          </div>
        ) : null}

        {/* Done */}
        {!selectedOpen ? (
          <div className="flex items-center justify-between gap-3 rounded-[16px] border-[1.5px] border-[#e6edf1] bg-[#f8fafc] px-4 py-3.5">
            <div className="flex items-center gap-2.5">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#15776d" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12.5l4.5 4.5L19 7.5" />
              </svg>
              <div className="text-[17px] font-bold text-[#475569]">
                {selected.triage.done_line || "Done"}
              </div>
            </div>
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleReopen()}
              className="text-[15px] font-bold text-[#94a3b8] hover:text-[#64748b]"
            >
              Reopen
            </button>
          </div>
        ) : null}
      </div>

      {assignOpen ? (
        <AssignSheet
          people={assignPeople}
          pendingPath={pendingPath}
          onClose={() => {
            setAssignOpen(false);
            setPendingPath(null);
          }}
          onSkip={() => {
            if (pendingPath === "restock" || pendingPath === "vendor") {
              void applyProgress(pendingPath, null);
            } else {
              setAssignOpen(false);
              setPendingPath(null);
            }
          }}
          onPick={(p) => {
            if (pendingPath === "restock" || pendingPath === "vendor") {
              void applyProgress(pendingPath, p.name, p.id);
            }
          }}
          onCustom={(name) => {
            if (pendingPath === "restock" || pendingPath === "vendor") {
              void applyProgress(pendingPath, name);
            }
          }}
        />
      ) : null}
    </div>
  ) : null;

  if (isMobile && selected) {
    return (
      <div className={cn("flex min-h-[70vh] flex-col bg-white", className)} data-tour="tour-issues-simple">
        <button
          type="button"
          onClick={() => {
            setSelectedId(null);
            setAssignOpen(false);
            setPendingPath(null);
          }}
          className="flex items-center gap-3 px-5 pb-3 pt-1"
        >
          <div className="flex h-[38px] w-[38px] items-center justify-center rounded-xl bg-[#f4f7f9]">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 6l-6 6 6 6" />
            </svg>
          </div>
          <span className="text-[15px] font-bold text-[#64748b]">Issues</span>
        </button>
        {detailBody}
      </div>
    );
  }

  return (
    <div className={cn("flex min-w-0 flex-col gap-5", className)} data-tour="tour-issues-simple">
      <div className="flex flex-wrap items-center justify-between gap-5">
        <h2 className="text-[30px] font-bold tracking-[-0.6px] text-[#0f1720] max-sm:text-[28px]">
          Issues
        </h2>
        <div className="max-sm:w-full">{filterBar}</div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16 text-[#64748b]">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : visible.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-[18px] border-2 border-dashed border-[#e6edf1] bg-[#f4f7f9] px-6 py-[72px] sm:bg-white sm:py-20">
          <div className="flex h-[58px] w-[58px] items-center justify-center rounded-2xl bg-[#e6edf1]">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#8494a2" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 13l4 4 10-10" />
            </svg>
          </div>
          <div className="text-[22px] font-bold text-[#0f1720]">No issues</div>
        </div>
      ) : (
        <>
          <div className="hidden overflow-hidden rounded-[18px] border-[1.5px] border-[#e6edf1] bg-white md:block">
            {visible.map((row) => renderListRow(row, false))}
          </div>
          <div className="flex flex-col gap-2.5 md:hidden">
            {visible.map((row) => renderListRow(row, true))}
          </div>
        </>
      )}

      <Dialog
        open={!!selected && !isMobile}
        onOpenChange={(o) => {
          if (!o) {
            setSelectedId(null);
            setAssignOpen(false);
            setPendingPath(null);
          }
        }}
      >
        <DialogContent className="gap-0 overflow-hidden rounded-[20px] p-0 sm:max-w-[460px]">
          <DialogTitle className="sr-only">
            {selected ? issueNote(selected) : "Issue"}
          </DialogTitle>
          {detailBody}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SimpleIssuesInbox;
